import { Component, inject, signal } from "@angular/core"
import { NgClass } from "@angular/common"
import { Router } from "@angular/router"
import { firstValueFrom } from "rxjs"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { FormField, form, submit } from "@angular/forms/signals"
import { BENEFIT_FIELD_LABELS, CategoryTypes, CreateBenefit, CreateBenefitFactory } from "../../models"
import { BenefitsService, ToastService } from "../../services"
import { TableComponent } from "../../components/table/table.component"
import { FiltersComponent } from "../../components/filters/filters.component"
import { FiltersValue, emptyFilters, toDependencies } from "../../helpers/DependenciesForm"
import { invalidFieldsDetail, toServerFieldErrors } from "../../helpers/FormErrors"
import { BooleanInputComponent } from "../../components/boolean-input/boolean-input.component"
import { FileInputComponent } from "../../components/file-input/file-input.component"
import {
    CategoriesInputComponent,
    DictionaryInputComponent,
} from "../../components"

@Component({
    selector: "valben-benefits-add",
    templateUrl: "./benefits-add.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        NgClass,
        MatProgressSpinnerModule,
        FormField,
        FiltersComponent,
        DictionaryInputComponent,
        BooleanInputComponent,
        FileInputComponent,
        CategoriesInputComponent,
    ],
})
export class BenefitsAddPage {
    private _benefitsService = inject(BenefitsService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)

    categoriesType = CategoryTypes.Benefit

    saving = signal(false)

    model = signal<CreateBenefit>(CreateBenefitFactory.create())
    benefitForm = form(this.model)

    filters = signal<FiltersValue>(emptyFilters())

    save(): void {
        this._create(false)
    }

    draft(): void {
        this._create(true)
    }

    back(): void {
        this._router.navigate(["/benefits"])
    }

    private async _create(isDraft: boolean): Promise<void> {
        this.benefitForm.isDraft().value.set(isDraft)
        this.benefitForm.dependencies().value.set(toDependencies(this.filters()))
        this.saving.set(true)
        let failed = false

        const created = await submit(this.benefitForm, {
            action: async f => {
                try {
                    await firstValueFrom(this._benefitsService.Create(f().value()))
                    return null
                } catch (err) {
                    failed = true
                    const { detail, errors } = toServerFieldErrors(err, this.benefitForm, BENEFIT_FIELD_LABELS)
                    this._error(detail)
                    return errors
                }
            },
            onInvalid: () => this._error(invalidFieldsDetail(this.benefitForm, BENEFIT_FIELD_LABELS)),
        })

        this.saving.set(false)
        if (failed || !created) return

        this._messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Uspješno uneseno",
            life: 10000,
        })
        TableComponent.resetPageOffset()
        this.back()
    }

    private _error(detail: string): void {
        this._messageService.add({ severity: "error", summary: "Error", detail, life: 10000 })
    }
}
